<template>
  <div class="map" ref="mapRoot">
    <!-- Remove the heading that might be overlapping the map -->
    <HeatmapLegend :visible="heatmapEnabled" :field="heatmapField" />

    <!-- At-a-glance transaction tooltip (RED-255): pure CSS, no network on hover -->
    <div
      v-if="hoverTooltip.visible"
      class="absolute z-20 pointer-events-none max-w-72 rounded-md bg-white/95 dark:bg-surface-800/95 shadow-lg border border-surface-200 dark:border-surface-700 px-3 py-2"
      :style="tooltipStyle"
      data-testid="txn-map-tooltip"
    >
      <template v-if="hoverTooltip.isCluster">
        <div class="text-sm font-semibold text-surface-900 dark:text-surface-0">
          {{ hoverTooltip.count }} tehingut
          <span v-if="tooltipClusterMedian" class="font-normal text-surface-500">
            · {{ tooltipClusterMedian }}
          </span>
        </div>
        <div v-if="tooltipClusterPeriod" class="text-xs text-surface-600 dark:text-surface-400 mt-0.5">
          {{ tooltipClusterPeriod }}
        </div>
        <div v-if="hoverTooltip.address" class="text-xs text-surface-600 dark:text-surface-400 mt-1 truncate">
          {{ hoverTooltip.address }}
        </div>
      </template>
      <template v-else>
        <div class="text-sm font-semibold text-surface-900 dark:text-surface-0">
          {{ tooltipPriceLine }}
          <span v-if="tooltipUnitPrice" class="font-normal text-surface-500">
            · {{ tooltipUnitPrice }}
          </span>
        </div>
        <div v-if="tooltipAreaLine" class="text-xs text-surface-600 dark:text-surface-400 mt-0.5">
          {{ tooltipAreaLine }}
        </div>
        <div class="flex items-center gap-1.5 mt-1 text-xs">
          <span
            class="px-1.5 py-0.5 rounded-sm font-medium"
            :class="hoverTooltip.land ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'"
          >
            {{ tooltipTypeLabel }}
          </span>
          <span v-if="hoverTooltip.address" class="truncate text-surface-600 dark:text-surface-400">
            {{ hoverTooltip.address }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import centroid from '@turf/centroid'
import { debounce } from 'lodash-es'
import { Map, View } from 'ol'
import { platformModifierKeyOnly } from 'ol/events/condition'
import { boundingExtent } from 'ol/extent'
import Feature from 'ol/Feature'
import Overlay from 'ol/Overlay'
import GeoJSON from 'ol/format/GeoJSON'
import LineString from 'ol/geom/LineString'
import Point from 'ol/geom/Point'
import {
  DragPan,
  defaults as defaultInteractions,
  MouseWheelZoom,
} from 'ol/interaction'
import { Layer } from 'ol/layer'
import HeatmapLayer from 'ol/layer/Heatmap'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import WebGLVectorLayer from 'ol/layer/WebGLVector'
import { Source } from 'ol/source'
import Cluster from 'ol/source/Cluster'
import VectorSource from 'ol/source/Vector'
// bboxStrategy removed — we manage loading manually to avoid re-fetching on pan
import { storeToRefs } from 'pinia'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from 'vue'
import {
  DATA_PROJECTION,
  HEATMAP_GRADIENT,
  MAP_PROJECTION,
  TRANSACTION_MAP_BROKER_OVERLAY_LAYER,
  TRANSACTION_MAP_COMPARISON_FEATURE_LAYER,
  TRANSACTION_MAP_FULL_LOAD_ZOOM,
  TRANSACTION_MAP_HOVER_LAYER,
  TRANSACTION_MAP_SEARCH_HIGHLIGHT_LAYER,
} from '@/constants'
import { useTransactionMapHover } from '@/composables/useTransactionMapHover'
import { useTransactionParcels } from '@/composables/useTransactionParcels'
import { drawMap } from '@/services/map/ExportService'
import { createFeatures } from '@/services/map/FeatureService'
import { createLayer } from '@/services/map/LayerService'
import {
  defaultProjection,
  getProjection,
  registerProjection,
} from '@/services/map/ProjectionService'
import {
  createHoverHighlightStyle,
  createLabelOnlyStyle,
  createStyle,
  createTransactionsStyle,
  createWebGLStyleArray,
} from '@/services/map/StyleService'
import { isWebGLSupported } from '@/services/map/webglSupport'
import { useBrokerSelectionStore } from '@/stores/brokerSelectionStore'
import { useMapStore } from '@/stores/map'
import { useRecentlyViewedStore } from '@/stores/recentlyViewed'
import { useTransactionStore } from '@/stores/transaction'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import HeatmapLegend from '@/components/map/HeatmapLegend.vue'

const mapStore = useMapStore()
const {
  label: activeLabel,
  activeBaseLayer,
  activeInfoLayers,
  heatmapEnabled,
  heatmapField,
} = storeToRefs(mapStore)

const transactionStore = useTransactionStore()
const { getFilterMetadata } = transactionStore
const {
  transactionMapRequestParams,
  transactionsAbortController,
  selectedTransaction,
  markedTransactions,
} = storeToRefs(transactionStore)

// RED-236 — recently-viewed drives the per-marker "viewed" badge + open halo.
const recentlyViewedStore = useRecentlyViewedStore()
const { viewedIds } = storeToRefs(recentlyViewedStore)

const evaluationStore = useEvaluationStore()
const brokerSelectionStore = useBrokerSelectionStore()
const { selectedIds: brokerSelectedIds } = storeToRefs(brokerSelectionStore)

// Cadastre outlines for transaction parcels (RED-255)
const {
  createParcelLayer,
  syncParcels,
  clearParcels,
  disposeParcels,
  getParcelsByTransactionId,
  getParcelsAtCoordinate,
} = useTransactionParcels()

// Canvas layer the hovered marker/parcels are cloned onto — brings the
// hovered feature forward (WebGL layers have no per-feature z-order)
const hoverHighlightLayer = shallowRef(null)
const hoverHighlightSource = shallowRef(null)

// Hover cursor + highlight + at-a-glance tooltip (RED-255)
const {
  tooltip: hoverTooltip,
  handlePointerMove: handleHoverMove,
  clearHover,
} = useTransactionMapHover({
  getMap: () => map.value,
  // Wrapped: declared later in the file; only ever invoked per-event
  findMarkerAtPixel: pixel => findMarkerAtPixel(pixel),
  findFeatureById: id => featureIndex.get(String(id)),
  findClusterByMemberId: id => clusterByMemberId.get(String(id)),
  getHighlightSource: () => hoverHighlightSource.value,
  getParcelsByTransactionId,
  getParcelsAtCoordinate,
})

const tooltipStyle = computed(() => {
  const el = mapRoot.value
  const flipX = el && hoverTooltip.x > el.clientWidth - 300
  const flipY = el && hoverTooltip.y > el.clientHeight - 130
  return {
    left: `${hoverTooltip.x + (flipX ? -12 : 14)}px`,
    top: `${hoverTooltip.y + (flipY ? -12 : 14)}px`,
    transform: `translate(${flipX ? '-100%' : '0'}, ${flipY ? '-100%' : '0'})`,
  }
})

const euroFormat = new Intl.NumberFormat('et-EE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
})

const tooltipPriceLine = computed(() =>
  hoverTooltip.price != null ? euroFormat.format(hoverTooltip.price) : '–',
)

const tooltipUnitPrice = computed(() => {
  if (hoverTooltip.price == null || !hoverTooltip.area) return null
  // Hectare-unit areas (land) price per hectare — €/m² rounds to a useless 0
  if (hoverTooltip.areaUnit === 'H') {
    return `${Math.round(hoverTooltip.price / hoverTooltip.area).toLocaleString('et-EE')} €/ha`
  }
  const perM2 = hoverTooltip.price / hoverTooltip.area
  if (perM2 < 1) return null
  const value = perM2 < 10 ? Math.round(perM2 * 10) / 10 : Math.round(perM2)
  return `${value.toLocaleString('et-EE')} €/m²`
})

const tooltipAreaLine = computed(() => {
  const parts = []
  if (hoverTooltip.area != null) {
    const unit = hoverTooltip.areaUnit === 'H' ? 'ha' : 'm²'
    parts.push(`${hoverTooltip.area.toLocaleString('et-EE')} ${unit}`)
  }
  if (hoverTooltip.date) {
    const [y, m, d] = hoverTooltip.date.split('-')
    parts.push(`${d}.${m}.${y}`)
  }
  // Multi-parcel deal: say how many cadastral units the price covers
  if (hoverTooltip.parcelCount > 1) {
    parts.push(`${hoverTooltip.parcelCount} katastriüksust`)
  }
  return parts.join(' · ')
})

const tooltipTypeLabel = computed(() =>
  hoverTooltip.land ? 'Maatükk' : hoverTooltip.objektiLiik || '–',
)

// Same formatting rules as the single-marker unit price: 1 decimal under
// 10 €/m², whole euros above, hidden when it rounds to a useless 0
const tooltipClusterMedian = computed(() => {
  const median = Number(hoverTooltip.medianLabel)
  if (hoverTooltip.medianLabel == null || !Number.isFinite(median) || median < 1) {
    return null
  }
  const value = median < 10 ? Math.round(median * 10) / 10 : Math.round(median)
  return `mediaan ${value.toLocaleString('et-EE')} €/m²`
})

const formatMonthYear = iso => {
  if (!iso) return null
  const [y, m] = iso.split('-')
  return `${m}.${y}`
}

const tooltipClusterPeriod = computed(() => {
  const from = formatMonthYear(hoverTooltip.dateMin)
  const to = formatMonthYear(hoverTooltip.dateMax)
  if (!from && !to) return null
  return from === to ? from : `${from ?? '…'} – ${to ?? '…'}`
})

const props = defineProps({
  twoFingerPanZoom: {
    type: Boolean,
    default: false,
  },
  center: {
    type: Array,
    default: () => [0, 0],
  },
  extent: {
    type: Array,
    default: () => [40500, 5993000, 1064500, 7017000],
  },
  bbox: {
    type: Array,
    default: () => [0, 0, 0, 0],
  },
  zoom: {
    type: Number,
    default: 1,
  },
  minZoom: {
    type: Number,
    default: 1,
  },
  maxZoom: {
    type: Number,
    default: 20,
  },
  projection: {
    type: String,
    default: defaultProjection,
  },
  baseLayers: {
    type: Array,
    default: () => [],
  },
  infoLayers: {
    type: Array,
    default: () => [],
  },
  overlay: {
    type: Object,
    default: () => {},
  },
  baseLayer: {
    type: String,
    default: '',
  },
  connectFeaturesCollection: {
    type: Boolean,
    default: false,
  },
  connectFeaturesMinZoom: {
    type: Number,
    default: 0,
  },
  enableSelectFeature: {
    type: Boolean,
    default: false,
  },
  enableGetFeatureInfo: {
    type: Boolean,
    default: false,
  },
  featureInfoServiceType: {
    type: String,
    default: 'WMS',
  },
  zoomToFeatures: {
    type: Boolean,
    default: false,
  },
  enableHover: {
    type: Boolean,
    default: false,
  },
  /**
   * RED-180 — only render the broker-selection orange "+" rings when this
   * map instance is rendered inside the standalone broker flow (Tehingute
   * kaart without an objectId). Other contexts that mount OpenLayerMap
   * (e.g. the evaluation-detail `TransactionsDialog`) must NOT show those
   * rings even if `brokerSelectionStore` still has selections from an
   * earlier visit to /transactions — that would leak the broker overlay
   * into the Hindamine flow.
   */
  brokerOverlayEnabled: {
    type: Boolean,
    default: false,
  },
  // RED-236 — gate the "viewed" badge, the open-marker halo, and recording an
  // open into the recently-viewed list to the standalone Tehingute kaart only.
  // Off on the evaluation-page TransactionsDialog so this shared map component
  // neither changes those markers' visuals nor pollutes the per-user list.
  recentlyViewedEnabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['viewport-change', 'features-selected', 'ready'])

const mapRoot = ref(null)
const layers = shallowRef({})

// shallowRef: only the assignment (init/teardown) is reactive. A deep ref
// would proxy the entire OL Map graph — a Proxy trap on every property
// access in hot paths (per-frame hover hit-tests) and raw-vs-proxy identity
// mismatches against objects from OL event callbacks.
const map = shallowRef(null)
// const activeBaseLayer = ref(null)
const featureSource = shallowRef(null)
const bbox = ref(props.bbox)
const zoom = ref(props.zoom)
const center = ref(props.center)
const overlay = ref(props.overlay)

const interactions = props.twoFingerPanZoom
  ? defaultInteractions({ dragPan: false, mouseWheelZoom: false }).extend([
      new DragPan({
        condition: platformModifierKeyOnly,
      }),
      new MouseWheelZoom({
        condition: platformModifierKeyOnly,
      }),
    ])
  : defaultInteractions()

onMounted(() => {
  if (props.projection && props.projection !== defaultProjection) {
    const projExists = getProjection(props.projection)
    // console.log('onMounted projExists', projExists)

    if (!projExists) {
      registerProjection(props.projection)
      // console.log('onMounted registerProjection', props.projection)
    }
  }

  init()

  // Set up listener for transaction updates to update map icons in real-time
  const unsubscribe = transactionStore.$onAction(({ name, args, after }) => {
    if (name === 'updateTransaction') {
      after(() => {
        updateFeatureIcon(args[0])
      })
    }
  })

  // Store unsubscribe for cleanup
  onBeforeUnmount(() => {
    unsubscribe()
  })

  // console.log('onMounted initLayerGroup layergroups', layerGroups.value)
})

const init = async () => {
  await initLayers()
  await initLayerGroup()
  await initMap()
  await setFeatureSource()

  map.value.setLayers(layerGroups.value)
  enableClickSelect()

  emit('ready')
}

const addTestFeature = () => {
  const featureLayer = getLayer('overlays', props.overlay.id)

  if (!featureLayer || !featureSource.value) {
    console.error('Feature layer or source not available', {
      layer: featureLayer,
      source: featureSource.value,
    })
    return
  }

  try {
    // Clear any existing features
    featureSource.value.clear()

    // Create test features with different styles based on OpenLayers example format
    // These match the images the user showed us (blue, orange, green markers)
    const features = [
      {
        type: 'Feature',
        properties: {
          id: 83264,
          _icon: 'default', // Blue marker,
        },
        geometry: {
          type: 'Point',
          coordinates: [541546.04, 6588228.34],
        },
      },
      {
        type: 'Feature',
        properties: {
          id: 83265,
          _icon: 'ad_link', // Orange marker
        },
        geometry: {
          type: 'Point',
          coordinates: [541646.04, 6588228.34],
        },
      },
      {
        type: 'Feature',
        properties: {
          id: 83266,
          _icon: 'marked_for_comparison', // Green marker
          _label: '150000',
        },
        geometry: {
          type: 'Point',
          coordinates: [541746.04, 6588228.34],
        },
      },
    ]

    // Create OpenLayers features from GeoJSON
    const fset = createFeatures({
      type: 'FeatureCollection',
      features: features,
    })

    // Add features to the source
    featureSource.value.addFeatures(fset)

    // Force a map render update
    map.value.render()

    // Fit the map to show the features
    fitToLayer(featureSource.value)
  } catch (e) {
    console.error('Error adding test features:', e)
  }
}

/**
 * Update feature icon in real-time when transaction data changes
 */
const updateFeatureIcon = transaction => {
  if (!featureSource.value || !transaction) {
    return
  }

  // Determine new icon using same precedence as backend
  let icon = 'default'
  if (transaction.is_marked || transaction.marked_for_comparison) {
    icon = 'marked_for_comparison'
  } else if (transaction.not_vabaturu_tehing) {
    icon = 'not_vabaturu_tehing'
  } else if (transaction.ad_link) {
    icon = 'ad_link'
  } else if (transaction.ad_link_not_found) {
    icon = 'ad_link_not_found'
  }

  // O(1) lookup via the id index instead of scanning the whole source
  const feature = featureIndex.get(String(transaction.id))

  if (feature) {
    // Use 'icon' property (not '_icon') for WebGL GLSL compatibility
    feature.set('icon', icon)
    // Force style refresh - this will trigger cluster re-styling if feature is clustered
    feature.changed()

    // Get the layer and check if it has a cluster source that needs refreshing
    const layer = getLayer('overlays', props.overlay?.id)
    if (layer) {
      const layerSource = layer.getSource()
      if (layerSource instanceof Cluster) {
        // Trigger cluster recalculation
        layerSource.changed()
      }
    }
  }
}

// Single-source approach for map features (simplified from double-buffer)
// In-place feature updates prevent flicker without needing crossfade animation
const hasLoadedOnce = ref(false) // Tracks if initial load is complete
const lastLoadedZoom = ref(null) // Tracks zoom level to detect zoom changes (clusters need clearing on zoom)
const lastLoadedContext = ref(null) // Tracks filters + label to detect context changes (requires full clear)
const featureLoaderRef = shallowRef(null) // Reference to loader function for direct invocation
const featureLayer = shallowRef(null) // Single WebGL layer

// id -> Feature index so per-feature updates (icon, viewed state) never linear-scan.
// globalThis.Map: the bare `Map` identifier is shadowed by the OpenLayers Map import.
const featureIndex = new globalThis.Map()
// member transaction id -> cluster Feature, so a parcel whose owners are
// clustered away can still resolve a hover tooltip to their cluster
const clusterByMemberId = new globalThis.Map()

// Baseline for applyViewedState's diff — the viewed-id set the currently
// loaded features were last stamped against.
let lastAppliedViewedIds = new Set()

const rebuildFeatureIndex = features => {
  featureIndex.clear()
  clusterByMemberId.clear()
  features.forEach(feature => {
    const id = feature.get('id') ?? feature.getId()
    if (id != null) {
      featureIndex.set(String(id), feature)
    }
    if (feature.get('cluster')) {
      ;(feature.get('point_ids') || []).forEach(memberId => {
        clusterByMemberId.set(String(memberId), feature)
      })
    }
  })
  // Fresh features carry the store-derived `viewed` stamp from
  // transformProperties — re-anchor the diff baseline to match.
  lastAppliedViewedIds = viewedIds.value
}

// JS hit-test for the transaction markers (hover + click). The GPU path
// (getFeaturesAtPixel on a WebGL layer) re-renders a hit buffer and stalls on
// a synchronous gl.readPixels — per rAF while hovering. Markers are points
// with a fixed-size icon, so an R-tree query + screen-space box test replaces
// it exactly. Icon geometry: 28×32 CSS px (marker and cluster SVGs alike),
// anchored [0.5, 1] (the coordinate is the pin tip), plus the 5px tolerance
// the GPU path used.
const ICON_HIT_BOX = { halfWidth: 19, above: 37, below: 5 }

const findMarkerAtPixel = pixel => {
  if (!map.value || !featureSource.value) {
    return null
  }
  const coordinate = map.value.getCoordinateFromPixel(pixel)
  const resolution = map.value.getView().getResolution()
  if (!coordinate || !resolution) {
    return null
  }
  const [x, y] = coordinate
  const candidates = featureSource.value.getFeaturesInExtent([
    x - ICON_HIT_BOX.halfWidth * resolution,
    y - ICON_HIT_BOX.above * resolution,
    x + ICON_HIT_BOX.halfWidth * resolution,
    y + ICON_HIT_BOX.below * resolution,
  ])
  let best = null
  let bestDistSq = Infinity
  for (const feature of candidates) {
    const coords = feature.getGeometry()?.getCoordinates?.()
    const featurePixel = coords && map.value.getPixelFromCoordinate(coords)
    if (!featurePixel) {
      continue
    }
    const dx = pixel[0] - featurePixel[0]
    const dy = pixel[1] - featurePixel[1]
    // Inside the icon's screen rect? (dy negative = pointer above the pin tip)
    if (
      dx < -ICON_HIT_BOX.halfWidth ||
      dx > ICON_HIT_BOX.halfWidth ||
      dy < -ICON_HIT_BOX.above ||
      dy > ICON_HIT_BOX.below
    ) {
      continue
    }
    // Nearest icon head wins when icons overlap (head ≈ 16px above the tip)
    const headDy = dy + 16
    const distSq = dx * dx + headDy * headDy
    if (distSq < bestDistSq) {
      bestDistSq = distSq
      best = feature
    }
  }
  return best
}

// RED-236 reconciliation: the live "viewed" sync is driven by the recently-viewed
// store (the single source of truth) via `applyViewedState()` below — it stamps
// AND un-stamps (remove/clear), which the earlier add-only session-set approach
// could not. Loaded features get their initial `viewed` stamp in transformProperties.

// Label layer for text rendering (VectorLayer on top of WebGL layers)
// WebGL cannot render text natively, so we use a hybrid approach
const labelLayer = shallowRef(null)
const labelSource = shallowRef(null)

// Heatmap layer for value density visualization
const heatmapLayer = shallowRef(null)
const heatmapSource = shallowRef(null)

/**
 * Sync features to the label layer for hybrid text rendering.
 * Only single features with labels — those need declutter, which the WebGL
 * text overlay lacks. Cluster counts render on the WebGL layer itself
 * (OL ≥ 10.10 text support).
 * @param {Array} features - OpenLayers features to sync
 */
const syncToLabelLayer = features => {
  if (!labelSource.value) {
    return
  }
  // Plain markers would just burden the canvas layer's render loop.
  const labelled = features.filter(
    feature => !feature.get('cluster') && feature.get('label') != null,
  )
  labelSource.value.clear()
  labelSource.value.addFeatures(labelled)
}

/**
 * Sync features to the heatmap layer for value density visualization.
 * Extracts numeric field values and normalizes them for heatmap weight.
 * @param {Array} features - OpenLayers features to sync
 */
const syncToHeatmapLayer = features => {
  if (!heatmapSource.value || !heatmapEnabled.value) return

  const fieldKey = heatmapField.value
  const heatmapFeatures = []

  features.forEach(feature => {
    const props = feature.getProperties()

    // Use pre-calculated heatmap weight from backend (percentile-normalized 0-1)
    const weight = props[`heatmap_weight_${fieldKey}`]

    if (weight != null && weight >= 0) {
      const heatFeature = feature.clone()
      heatFeature.set('weight', Math.max(0.1, weight)) // Min weight 0.1 for visibility
      heatmapFeatures.push(heatFeature)
    }
  })

  heatmapSource.value.clear()
  heatmapSource.value.addFeatures(heatmapFeatures)
}

/**
 * Calculate heatmap blur and radius based on zoom level.
 * Matches backend getClusterDistanceForZoom formula for visual consistency.
 * @param {number} zoom - Current map zoom level
 * @returns {{ blur: number, radius: number }} Heatmap parameters
 */
const getHeatmapParamsForZoom = zoom => {
  const baseDistance = 2
  const clusterDistance = Math.max(
    1,
    baseDistance * Math.pow(2, 16 - Math.min(zoom, 18)),
  )

  // Scale blur/radius proportionally to cluster distance
  // At zoom 18: distance=1, blur=8, radius=6 (small, detailed)
  // At zoom 10: distance=128, blur=30, radius=20 (large, spread)
  const blur = Math.min(30, Math.max(8, 8 + Math.log2(clusterDistance) * 3))
  const radius = Math.min(20, Math.max(6, 6 + Math.log2(clusterDistance) * 2))

  return { blur, radius }
}

/**
 * Update heatmap layer blur and radius based on zoom level.
 * @param {number} zoom - Current map zoom level
 */
const updateHeatmapParams = zoom => {
  if (!heatmapLayer.value) return

  const { blur, radius } = getHeatmapParamsForZoom(zoom)
  heatmapLayer.value.setBlur(blur)
  heatmapLayer.value.setRadius(radius)
}

/**
 * Trigger feature load WITHOUT calling refresh().
 * This avoids the synchronous clear() inside VectorSource.refresh() which causes flicker.
 * The loader handles in-place feature updates to preserve visible features during pan.
 */
const triggerFeatureLoad = () => {
  if (!map.value || !featureLoaderRef.value) return
  featureLoaderRef.value()
}

// Debounced refresh to handle rapid zoom/filter changes
const debouncedRefresh = debounce(() => {
  if (!featureSource.value) return
  triggerFeatureLoad()
}, 150)

const addFeaturesToMap = ({ immediate = false } = {}) => {
  // Filter/search churn stays debounced; the moveend path is already
  // debounced upstream (handleMoveEnd), so stacking a second delay on it
  // only added load latency. A pending debounced refresh is left alone — it
  // may carry a newer filter context than this moveend; if it doesn't, the
  // loader's in-flight/early-return guards drop it for free.
  if (immediate) {
    triggerFeatureLoad()
  } else {
    debouncedRefresh()
  }

  if (props.zoomToFeatures) {
    fitToLayer(featureSource.value)
  }
}

const addGeometryFeature = geom => {
  // console.log('addFeature', geom)
  //const featureLayer = getLayer('overlays', props.overlay.id)

  let comparisonFeatureLayer = getLayer(
    'overlays',
    TRANSACTION_MAP_COMPARISON_FEATURE_LAYER,
  )

  if (!comparisonFeatureLayer) {
    comparisonFeatureLayer = createLayer({
      id: TRANSACTION_MAP_COMPARISON_FEATURE_LAYER,
      type: 'FeatureCollection',
      style: createStyle({
        stroke: {
          color: 'red',
          width: 2,
        },
      }),
      clusters: false,
      zIndex: 1200,
      webgl: false,
      declutter: false,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    })

    addLayerTo('overlays', comparisonFeatureLayer)
  }

  // console.log('comparisonFeatureLayer', comparisonFeatureLayer)
  comparisonFeatureLayer.setVisible(true)

  const feature = createFeatures({
    type: 'Feature',
    geometry: geom,
  })

  const comparisonSource = comparisonFeatureLayer.getSource()

  comparisonSource.addFeatures(feature)

  const point = centroid(geom)
  panTo(point.geometry.coordinates)
  zoomTo(12)
}

/**
 * Add a search highlight feature to the map (for address/cadastre search results)
 * @param {Object} location - Location object with geometry (for cadastre) or viitepunkt_x/y (for addresses)
 */
const addSearchHighlight = location => {
  // Clear any existing search highlights first
  clearSearchHighlight()

  let searchHighlightLayer = getLayer(
    'overlays',
    TRANSACTION_MAP_SEARCH_HIGHLIGHT_LAYER,
  )

  if (!searchHighlightLayer) {
    searchHighlightLayer = createLayer({
      id: TRANSACTION_MAP_SEARCH_HIGHLIGHT_LAYER,
      type: 'FeatureCollection',
      style: createStyle({
        stroke: {
          color: '#6366f1', // Indigo color for search highlights
          width: 3,
        },
        fill: {
          color: 'rgba(99, 102, 241, 0.15)',
        },
        image: {
          type: 'circle',
          radius: 12,
          fill: {
            color: '#6366f1',
          },
          stroke: {
            color: '#ffffff',
            width: 3,
          },
        },
      }),
      clusters: false,
      zIndex: 1300,
      webgl: false,
      declutter: false,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    })

    addLayerTo('overlays', searchHighlightLayer)
  }

  searchHighlightLayer.setVisible(true)

  const searchSource = searchHighlightLayer.getSource()

  // If we have a polygon/geometry, add it
  if (location.geometry) {
    const feature = createFeatures({
      type: 'Feature',
      geometry: location.geometry,
    })
    searchSource.addFeatures(feature)
  } else if (location.viitepunkt_x && location.viitepunkt_y) {
    // For addresses, create a point feature
    const feature = createFeatures({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [location.viitepunkt_x, location.viitepunkt_y],
      },
    })
    searchSource.addFeatures(feature)
  }
}

/**
 * Clear the search highlight layer
 */
const clearSearchHighlight = () => {
  const searchHighlightLayer = getLayer(
    'overlays',
    TRANSACTION_MAP_SEARCH_HIGHLIGHT_LAYER,
  )

  if (searchHighlightLayer) {
    const source = searchHighlightLayer.getSource()
    if (source) {
      source.clear()
    }
  }
}

/**
 * RED-180 — broker selection overlay.
 *
 * Draws a small orange ring on top of each transaction the user has
 * picked for the broker .docx export. Lives on its own non-WebGL vector
 * layer above the main feature layer so it never collides with the
 * Hindamine "marked_for_comparison" red-icon swap (that one mutates the
 * underlying feature's `icon` property; this one is purely additive).
 *
 * Geometries come from the main feature source — if the user is zoomed
 * out / panned away from a selected transaction, no ring is drawn for it
 * because the source doesn't carry that feature in the current viewport.
 * That's acceptable for v1; the dialog preview is the authoritative list.
 */
const syncBrokerOverlay = () => {
  // Context gate: never render broker rings outside the standalone broker
  // flow, even if the Pinia selection store still has entries from an
  // earlier /transactions visit (e.g. user navigated to an evaluation and
  // opened the TransactionsDialog without clearing).
  if (!props.brokerOverlayEnabled) {
    const existingLayer = getLayer('overlays', TRANSACTION_MAP_BROKER_OVERLAY_LAYER)
    existingLayer?.getSource()?.clear()
    return
  }

  let brokerLayer = getLayer('overlays', TRANSACTION_MAP_BROKER_OVERLAY_LAYER)

  if (!brokerLayer) {
    brokerLayer = createLayer({
      id: TRANSACTION_MAP_BROKER_OVERLAY_LAYER,
      type: 'FeatureCollection',
      // Solid orange disc with a white border + white "+" inside, anchored
      // to the top-right corner of the underlying transaction marker.
      // The orange matches PrimeVue's `warn` severity (Tailwind amber-500)
      // used by the sidebar broker "+" button so the two visual cues read
      // as the same control.
      //
      // OL pixel conventions: Circle `displacement` Y is positive-up;
      // Text `offsetY` is positive-down. The Y values therefore have
      // opposite signs but represent the same vertical position.
      style: createStyle({
        image: {
          type: 'circle',
          radius: 10,
          displacement: [14, 28],
          fill: { color: '#F59E0B' },
          stroke: { color: '#FFFFFF', width: 2 },
        },
        text: {
          text: '+',
          font: 'bold 16px sans-serif',
          fill: { color: '#FFFFFF' },
          offsetX: 14,
          offsetY: -28,
        },
      }),
      clusters: false,
      zIndex: 1250,
      webgl: false,
      declutter: false,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    })

    addLayerTo('overlays', brokerLayer)
  }

  const overlaySource = brokerLayer.getSource()
  if (!overlaySource) return

  overlaySource.clear()

  if (!featureSource.value || brokerSelectedIds.value.length === 0) {
    return
  }

  // ≤5 selected ids — resolve each through the O(1) feature index instead of
  // scanning the (potentially countrywide) feature set.
  const ringFeatures = []
  brokerSelectedIds.value.forEach(id => {
    const geom = featureIndex.get(String(id))?.getGeometry()
    if (geom) {
      ringFeatures.push(new Feature({ geometry: geom.clone() }))
    }
  })

  if (ringFeatures.length > 0) {
    overlaySource.addFeatures(ringFeatures)
  }
}

watch(
  [brokerSelectedIds, () => props.brokerOverlayEnabled],
  () => syncBrokerOverlay(),
  { deep: true },
)

const layerGroups = computed(() => {
  return Object.keys(layers.value).map(i => layers.value[i])
})

const initLayers = async () => {
  return new Promise((resolve, reject) => {
    layers.value = {
      base: createLayer({
        type: 'Group',
        layers: [],
      }),
      info: createLayer({
        type: 'Group',
        layers: [],
      }),
      overlays: createLayer({
        type: 'Group',
        layers: [],
      }),
    }

    nextTick(() => {
      resolve()
    })
  })
}

const initLayerGroup = async () => {
  const [baseLayers, infoLayers, overlay] = await Promise.all([
    createBaseLayers(props.baseLayers, props.baseLayer),
    createLayers('info', props.infoLayers),
    createOverlay(props.overlay),
  ])

  mapStore.$patch({
    layers: {
      base: baseLayers.map(layer => {
        return {
          id: layer.get('id'),
          label: layer.get('title'),
        }
      }),
      info: infoLayers.map(layer => {
        return {
          id: layer.get('id'),
          label: layer.get('title'),
        }
      }),
      overlays: overlay,
    },
  })
}

const initMap = async () => {
  return new Promise((resolve, reject) => {
    map.value = new Map({
      loadTilesWhileAnimating: true,
      loadTilesWhileInteracting: true,
      target: mapRoot.value,
      controls: [],
      interactions,
      moveTolerance: 2,
      // Match the device instead of forcing 2x — a hard-coded 2 quadruples the
      // fill cost on 1x displays (older devices). Capped at 2 for retina.
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
      view: new View({
        projection: props.projection,
        center: props.center,
        zoom: props.zoom,
        minZoom: props.minZoom,
        maxZoom: props.maxZoom,
        extent: props.extent,
      }),
    })

    nextTick(() => {
      map.value.on('moveend', handleMoveEnd)
      resolve()
    })
  })
}

const createBaseLayers = async (layersConf, activeBaseLayerId) => {
  return new Promise(async (resolve, reject) => {
    if (!layersConf || !layersConf.length) {
      return
    }
    const baseLayers = []

    await Promise.all(
      layersConf.map(async layerConf => {
        layerConf.visible = layerConf.id === activeBaseLayerId
        layerConf.zIndex = 0

        if (!('minZoom' in layerConf)) {
          layerConf.minZoom = props.minZoom
        }
        if (!('maxZoom' in layerConf)) {
          layerConf.maxZoom = props.maxZoom
        }

        const layer = createLayer(layerConf)
        addLayerTo('base', layer)

        baseLayers.push(layer)

        if (layerConf.visible) {
          activeBaseLayer.value = layer.get('id')
        }
      }),
    )
    resolve(baseLayers)
  })
}

const createLayers = (group, layersConf) => {
  return new Promise((resolve, reject) => {
    if (!layersConf || !layersConf.length) {
      return
    }
    layersConf.forEach(layerConf => {
      if (!('minZoom' in layerConf)) {
        layerConf.minZoom = props.minZoom
      }
      if (!('maxZoom' in layerConf)) {
        layerConf.maxZoom = props.maxZoom
      }
      addLayerTo(group, createLayer(layerConf))
    })
    resolve(layers.value[group].getLayers().getArray())
  })
}

const createOverlay = overlay => {
  if (!overlay) {
    return
  }

  // For feature overlays, create double-buffered WebGL layers for smooth crossfade
  if (
    overlay.id &&
    (overlay.id.includes('feature') || overlay.id.includes('transaction'))
  ) {
    // Create GeoJSON format for batch feature processing (much faster than individual creation)
    const geoJsonFormat = new GeoJSON({
      dataProjection: DATA_PROJECTION,
      featureProjection: MAP_PROJECTION,
    })

    // Transform underscore-prefixed properties to avoid WebGL/GLSL reserved identifier issues
    const transformProperties = props => {
      const transformed = {}
      for (const [key, value] of Object.entries(props || {})) {
        if (key === '_icon') transformed.icon = value
        else if (key === '_open') transformed.isOpen = value
        else if (key === '_selected') transformed.isSelected = value
        else if (key === '_label') transformed.label = value
        else if (key === '_land') transformed.land = value ? 1 : 0
        else if (key === '_price') transformed.price = value
        else if (key === '_area') transformed.area = value
        else if (key === '_area_unit') transformed.areaUnit = value
        else if (key === '_date') transformed.date = value
        else if (key === '_address') transformed.address = value
        else if (key === '_type') transformed.objektiLiik = value
        else transformed[key] = value
      }
      // WebGL style filters need these attributes on EVERY feature — stamp 0/1
      // so a missing attribute can never break the filter expressions.
      transformed.land = transformed.land ?? 0
      // RED-236: "viewed" is driven by the recently-viewed store (single source),
      // rendered as the corner badge (not RED-255's desaturation). Stamped on
      // every feature at load; live add/remove is handled by applyViewedState().
      transformed.viewed =
        !transformed.cluster &&
        transformed.id != null &&
        viewedIds.value.has(transformed.id)
          ? 1
          : 0
      // Cluster count for the WebGL text renderer (OL ≥ 10.10) — precomputed
      // string ('' on non-clusters) so every feature carries the attribute.
      transformed.count_label =
        transformed.cluster && transformed.point_count
          ? transformed.point_count > 99
            ? '99+'
            : String(transformed.point_count)
          : ''
      return transformed
    }

    // Process API response into OpenLayers features
    const processFeatures = responseData => {
      const validFeatures = responseData.features
        .filter(f => {
          const geom = f.geometry
          return (
            geom &&
            (geom.type === 'Point' ||
              (geom.type === 'GeometryCollection' &&
                geom.geometries?.[0]?.type === 'Point'))
          )
        })
        .map(f => ({
          type: 'Feature',
          geometry:
            f.geometry.type === 'GeometryCollection'
              ? f.geometry.geometries[0]
              : f.geometry,
          properties: transformProperties(f.properties),
          id: f.id || f.properties?.id,
        }))

      return geoJsonFormat.readFeatures({
        type: 'FeatureCollection',
        features: validFeatures,
      })
    }

    // Single-phase loading (RED-255): one request per load — countrywide
    // (bbox = null) below the threshold, a 5x-buffered viewport bbox above it.
    // The old viewport-first + background-merge phase pair caused duplicate /map
    // requests and could merge features fetched with NEW filters into a source
    // rendered with OLD filters (stale-feature leakage on filter switches).
    // No bboxStrategy — loads are triggered manually so panning never refetches.
    const ZOOM_THRESHOLD_FULL_LOAD = TRANSACTION_MAP_FULL_LOAD_ZOOM

    // Context changes must not blank the parcel layer eagerly: the reset is
    // deferred to the next SUCCESSFUL load so an aborted request keeps the
    // previous outlines instead of leaving the layer permanently empty.
    let parcelsNeedReset = false

    // Track the buffered extent for high-zoom loads
    const lastLoadedBufferedBbox = shallowRef(null)
    // Generation counter: only the newest request may touch the source
    let loadSequence = 0
    // Identity of the in-flight request — the filter path (debounced) and the
    // moveend path (immediate) can both ask for the same load inside the
    // in-flight window; the second identical ask is dropped, not re-sent.
    // The controller is checked so an ABORTED in-flight request never blocks
    // an identical retry (moveend aborts before it re-asks).
    let inFlightKey = null
    let inFlightController = null

    const calculateBufferedBbox = (extent, bufferFactor = 5) => {
      const width = extent[2] - extent[0]
      const height = extent[3] - extent[1]
      const bw = width * (bufferFactor - 1) / 2
      const bh = height * (bufferFactor - 1) / 2
      return [
        extent[0] - bw,
        extent[1] - bh,
        extent[2] + bw,
        extent[3] + bh,
      ]
    }

    const isExtentWithinBuffered = (viewExtent, bufferedExtent) => {
      if (!bufferedExtent) return false
      return (
        viewExtent[0] >= bufferedExtent[0] &&
        viewExtent[1] >= bufferedExtent[1] &&
        viewExtent[2] <= bufferedExtent[2] &&
        viewExtent[3] <= bufferedExtent[3]
      )
    }

    // Fetch features from API. Filters are passed in as a snapshot taken when the
    // load started — never read live from the store, so an in-flight request can
    // not silently pick up a newer filter state than the load that issued it.
    const fetchFeatures = async (requestFilters, bbox, abortSignal) => {
      const response = await axios.post(
        '/api/transactions/map',
        {
          filters: requestFilters,
          label: activeLabel.value,
          bbox: bbox,
          // Store zoom is stamped by the first moveend — the dialog's
          // auto-search (RED-255) fires before that, and a null zoom casts
          // to 0 server-side, clustering the results away. Same view
          // fallback as loadFeatures' currentZoom.
          zoom: transactionMapRequestParams.value.zoom ?? map.value?.getView()?.getZoom(),
          evaluation_id: evaluationStore.id,
          heatmap: heatmapEnabled.value,
        },
        { signal: abortSignal },
      )
      if (response.status === 200 && response.data.features) {
        return processFeatures(response.data)
      }
      return null
    }

    // Replace the full feature set atomically and resync auxiliary layers
    const replaceFeatures = features => {
      featureSource.value.clear()
      featureSource.value.addFeatures(features)
      rebuildFeatureIndex(features)
      syncToLabelLayer(features)
      syncToHeatmapLayer(features)
      syncBrokerOverlay()
      // Atomic parcel swap on the success path only; extent/zoom re-read from
      // the view because the user may have panned during the request.
      if (parcelsNeedReset) {
        clearParcels()
        parcelsNeedReset = false
      }
      const view = map.value?.getView()
      if (view) {
        // Features just landed in the source — extent query beats iterating
        // the full (possibly countrywide) array.
        const extent = view.calculateExtent(map.value.getSize())
        syncParcels(
          featureSource.value.getFeaturesInExtent(extent),
          Math.floor(view.getZoom()),
          extent,
          view,
        )
      }
    }

    const loadFeatures = async () => {
      if (!map.value || !featureSource.value) return

      const view = map.value.getView()
      // Mid-animation/interaction zoom levels are transient — fetching them
      // wastes a countrywide payload. Re-ask after the view settles.
      if (view.getAnimating() || view.getInteracting()) {
        debouncedRefresh()
        return
      }
      const viewExtent = view.calculateExtent(map.value.getSize())
      // Store zoom is stamped by the first moveend — fall back to the view so
      // a pre-moveend load doesn't record a phantom zoom (Math.floor(null)=0)
      // that the next pan would read as a zoom change and refetch on.
      const currentZoom = Math.floor(
        transactionMapRequestParams.value.zoom ?? view.getZoom(),
      )
      const currentContext = JSON.stringify({
        filters: transactionMapRequestParams.value.filters,
        label: activeLabel.value,
        evaluationId: evaluationStore.id,
      })
      const highZoom = currentZoom >= ZOOM_THRESHOLD_FULL_LOAD
      const contextChanged = lastLoadedContext.value !== currentContext

      // Skip when zoom and context are unchanged (pure pan) and, at high zoom,
      // the viewport is still inside the previously loaded buffered extent.
      if (
        hasLoadedOnce.value &&
        lastLoadedZoom.value === currentZoom &&
        !contextChanged &&
        (!highZoom || isExtentWithinBuffered(viewExtent, lastLoadedBufferedBbox.value))
      ) {
        // Panning can still reveal features whose parcels aren't drawn yet.
        // R-tree extent query — never a full scan of the countrywide set.
        syncParcels(
          featureSource.value.getFeaturesInExtent(viewExtent),
          Math.floor(view.getZoom()),
          viewExtent,
          view,
        )
        return
      }

      // Deep snapshot of the filters this load will request — only built when
      // a request is actually going out, not on every pure-pan moveend.
      const requestFilters = JSON.parse(currentContext).filters

      const requestBbox = highZoom ? calculateBufferedBbox(viewExtent) : null
      const requestKey = `${currentContext}|${currentZoom}|${JSON.stringify(requestBbox)}`
      if (
        requestKey === inFlightKey &&
        inFlightController &&
        !inFlightController.signal.aborted
      ) {
        return
      }

      if (contextChanged) {
        parcelsNeedReset = true
      }

      // Filter metadata only depends on filters — not on zoom or viewport
      // (and rides its own AbortController, so map-load aborts never kill it)
      if (contextChanged || !hasLoadedOnce.value) {
        getFilterMetadata()
      }

      // Any previous map load is obsolete the moment a new request goes out —
      // abort it here (and only here) so pans/zooms that DON'T need a request
      // leave in-flight loads alone.
      if (inFlightController && !inFlightController.signal.aborted) {
        transactionStore.abortAndResetTransactionsAbortController()
      }

      const sequence = ++loadSequence
      inFlightKey = requestKey
      inFlightController = transactionsAbortController.value

      mapStore.$patch({ loading: true })

      try {
        const features = await fetchFeatures(
          requestFilters,
          requestBbox,
          transactionsAbortController.value.signal,
        )

        // A newer load has started meanwhile — drop this stale response
        if (!features || sequence !== loadSequence || !featureSource.value) {
          return
        }

        replaceFeatures(features)
        hasLoadedOnce.value = true
        lastLoadedZoom.value = currentZoom
        lastLoadedContext.value = currentContext
        lastLoadedBufferedBbox.value = requestBbox
      } catch (error) {
        if (!axios.isCancel?.(error) && error.name !== 'CanceledError' && error.name !== 'AbortError') {
          console.error('Error loading transaction features:', error)
        }
      } finally {
        if (sequence === loadSequence) {
          mapStore.$patch({ loading: false })
          inFlightKey = null
          inFlightController = null
        }
      }
    }

    // Save loader reference for direct invocation
    featureLoaderRef.value = loadFeatures

    // Create vector source without loading strategy — we manage loading manually
    const newSource = new VectorSource({
      dataProjection: DATA_PROJECTION,
      featureProjection: MAP_PROJECTION,
    })

    // RED-236 — each feature's initial `viewed` (0/1) is stamped in
    // transformProperties from the recently-viewed store; live add/remove is
    // handled by applyViewedState(). No per-source addfeature listener needed.

    const conf = {
      default: { color: '#027BFA' },
      ad_link: { color: '#2DA74E' },
      ad_link_not_found: { color: '#FFC107' },
      not_vabaturu_tehing: { color: '#FF4081' },
      marked_for_comparison: { color: '#DB3848' },
      cluster: { color: '#027BFA' },
      cluster_ad_link: { color: '#2DA74E' },
      cluster_ad_link_not_found: { color: '#FFC107' },
      cluster_not_vabaturu_tehing: { color: '#FF4081' },
      cluster_marked_for_comparison: { color: '#DB3848' },
    }

    const zIndex = overlay.zIndex || 1000

    // Marker layer: GPU-accelerated when the browser has WebGL, canvas
    // otherwise. OL creates the WebGL context lazily on the first render
    // frame, so construction never throws on an unsupported browser — the
    // failure would land in the map's animation loop and white-screen the
    // whole map. Probe up front instead.
    let newLayer = null
    if (isWebGLSupported()) {
      try {
        newLayer = new WebGLVectorLayer({
          source: newSource,
          style: createWebGLStyleArray(),
          variables: { layerOpacity: 1, layerScale: 1 },
          // Hover/click resolve via findMarkerAtPixel (R-tree + icon box) — the
          // GPU hit-detection buffer would cost a hit render + sync readPixels
          // per query and is never consulted.
          disableHitDetection: true,
          updateWhileAnimating: true,
          updateWhileInteracting: true,
        })
      } catch (e) {
        console.warn('WebGL marker layer failed, falling back to canvas:', e)
      }
    }
    if (!newLayer) {
      newLayer = new VectorLayer({
        source: newSource,
        style: createTransactionsStyle(conf),
        declutter: false,
        updateWhileAnimating: true,
        updateWhileInteracting: true,
      })
    }

    // Store references
    featureSource.value = newSource
    featureLayer.value = newLayer
    hasLoadedOnce.value = false
    lastLoadedZoom.value = null
    lastLoadedContext.value = null

    // Set layer metadata
    newLayer.set('id', overlay.id)
    newLayer.setZIndex(zIndex)
    newLayer.set('conf', overlay)

    // Add layer to map
    addLayerTo('overlays', newLayer)

    if (newLayer instanceof WebGLVectorLayer) {
      // Label overlay layer (VectorLayer for text rendering): WebGL cannot
      // render text, so we use a hybrid approach with a separate layer. The
      // canvas marker style draws cluster counts and price labels itself — a
      // label layer alongside it would double every label.
      const labelVectorSource = new VectorSource()
      const labelVectorLayer = new VectorLayer({
        source: labelVectorSource,
        style: createLabelOnlyStyle(),
        zIndex: zIndex + 1, // Above WebGL layer
        // Colliding labels drop instead of overflowing onto neighbouring
        // icons — the icons live on the WebGL layer below, so per-feature
        // z-interleaving is impossible; decluttering is the fix. Unique
        // string: declutter groups are shared by value across layers.
        declutter: 'txn-labels',
        updateWhileAnimating: true,
        updateWhileInteracting: true,
      })
      labelVectorLayer.set('id', `${overlay.id}_labels`)
      labelLayer.value = labelVectorLayer
      labelSource.value = labelVectorSource
      // Always visible - cluster counts always need rendering, price labels are conditional via style function
      labelVectorLayer.setVisible(true)
      addLayerTo('overlays', labelVectorLayer)
    }

    // Create heatmap layer for value density visualization
    const heatmapVectorSource = new VectorSource()
    const initialHeatmapParams = getHeatmapParamsForZoom(props.zoom)
    const heatmapVectorLayer = new HeatmapLayer({
      source: heatmapVectorSource,
      blur: initialHeatmapParams.blur,
      radius: initialHeatmapParams.radius,
      gradient: HEATMAP_GRADIENT,
      weight: feature => feature.get('weight') || 0,
      visible: heatmapEnabled.value,
      zIndex: zIndex - 1, // Below transaction marker layer
      opacity: 1,
    })
    heatmapVectorLayer.set('id', `${overlay.id}_heatmap`)
    heatmapLayer.value = heatmapVectorLayer
    heatmapSource.value = heatmapVectorSource
    addLayerTo('overlays', heatmapVectorLayer)

    // Parcel boundaries under the markers, above the basemap (RED-255).
    // createParcelLayer runs the same WebGL-vs-canvas selection internally.
    addLayerTo('overlays', createParcelLayer(zIndex - 2))

    // Hover-highlight layer: the hovered marker clone + sibling parcels
    // render here, above everything interactive (RED-255)
    const highlightSource = new VectorSource()
    const highlightLayer = new VectorLayer({
      source: highlightSource,
      style: createHoverHighlightStyle(),
      zIndex: 1350,
      updateWhileAnimating: true,
      updateWhileInteracting: true,
    })
    highlightLayer.set('id', TRANSACTION_MAP_HOVER_LAYER)
    hoverHighlightSource.value = highlightSource
    hoverHighlightLayer.value = highlightLayer
    addLayerTo('overlays', highlightLayer)
  } else {
    // For other overlays, use the standard layer creation process
    createLayers('overlays', [overlay])
  }
}

const addLayerTo = (group, layer) => {
  layers.value[group].getLayers().push(layer)
  // triggerUpdate()
}

const getLayer = (group, id) => {
  if (group in layers.value && id) {
    return layers.value[group]
      .getLayers()
      .getArray()
      .find(layer => {
        return layer.get('id') === id
      })
  }
  return undefined
}

const changeBaseLayer = id => {
  const matchingLayers = layers.value.base
    .getLayers()
    .getArray()
    .filter(layer => layer.get('id') === id)

  if (matchingLayers.length === 1) {
    layerGroups.value[0].getLayers().forEach(layer => {
      layer.setVisible(false)
    })
    matchingLayers[0].setVisible(true)
    // this.triggerUpdate()
  }
}

const setFeatureSource = async () => {
  return new Promise((resolve, reject) => {
    if (!props.overlay) {
      resolve(null)
      return
    }

    // featureSource is set directly in createOverlay
    if (featureSource.value) {
      resolve(featureSource.value)
      return
    }

    // Fallback for non-WebGL layers
    const layer = getLayer('overlays', props.overlay.id)
    if (!layer) {
      resolve(null)
      return
    }

    const layerSource = layer.getSource()
    const source = layerSource instanceof Cluster ? layerSource.getSource() : layerSource

    if (source) {
      featureSource.value = source
    } else {
      console.error('Could not get source from layer:', layer)
    }

    resolve(source)
  })
}

const fitTo = bbox => {
  if (bbox.length) {
    map.value.getView().fit(bbox, {
      padding: [50, 50, 50, 50],
      maxZoom: props.maxZoom,
      duration: 250,
    })
    // triggerUpdate()
  }
}

const fitToLayer = layerOrSource => {
  let bbox = []
  if (layerOrSource instanceof Source) {
    bbox = layerOrSource.getExtent()
  }
  if (layerOrSource instanceof Layer) {
    bbox = layerOrSource.getSource().getExtent
      ? layerOrSource.getSource().getExtent()
      : layerOrSource.getExtent()
  }
  fitTo(bbox)
}

watch(activeBaseLayer, newVal => {
  if (newVal) {
    changeBaseLayer(newVal)
  }
})

watch(activeInfoLayers, newVal => {
  if (newVal && map.value) {
    layers.value.info
      ?.getLayers()
      ?.getArray()
      ?.forEach(layer => {
        layer.setVisible(newVal.includes(layer.get('id')))
      })
    map.value.render()
  }
})

// Watch activeLabel to toggle label layer visibility and trigger data refresh
watch(activeLabel, newVal => {
  if (labelLayer.value) {
    labelLayer.value.setVisible(!!newVal)
  }
  // Trigger source refresh to fetch features with label data from backend
  if (featureSource.value && hasLoadedOnce.value) {
    triggerFeatureLoad()
  } else if (featureSource.value) {
    featureSource.value.refresh()
  }
})

// Watch heatmap enabled state to toggle heatmap layer visibility and refetch data when enabled
watch(heatmapEnabled, enabled => {
  if (heatmapLayer.value) {
    heatmapLayer.value.setVisible(enabled)
  }
  // Trigger source refresh to fetch features with/without heatmap weights from backend
  if (featureSource.value && hasLoadedOnce.value) {
    triggerFeatureLoad()
  } else if (featureSource.value) {
    featureSource.value.refresh()
  }
})

// Watch heatmap field changes to update visualization
watch(heatmapField, () => {
  if (heatmapEnabled.value && featureSource.value) {
    syncToHeatmapLayer(featureSource.value.getFeatures())
  }
})

/*
watch(overlay, (newVal) => {
    if (newVal) {
        layers.value.overlays.getLayers().clear()
        createOverlay(newVal)
        setFeatureSource()
        if (props.zoomToFeatures) {
            fitToLayer(featureSource)
        }
    }
})

watch(bbox, (newVal) => {
    if (newVal) {
        if (newVal.length === 4) {
            fitTo(newVal)
        } else if (newVal.length === 5) {
            fitTo(boundingExtent(newVal))
        }
    }
})

watch(zoom, (newZoom, oldZoom) => {
    if (newZoom && newZoom !== oldZoom) {
        zoomTo(newZoom)
    }
})

watch(center, (newCenter, oldCenter) => {
    if (newCenter && newCenter.length === 2 &&
       (!oldCenter || newCenter[0] !== oldCenter[0] || newCenter[1] !== oldCenter[1])) {
        panTo(newCenter)
    }
}, { deep: true }) */

const animate = opts => {
  if (!map.value) return
  map.value.getView().animate({ ...opts, duration: 250 })
  // triggerUpdate()
}

const zoomIn = () => {
  if (!map.value) return
  animate({ zoom: map.value.getView().getZoom() + 1 })
}

const zoomOut = () => {
  if (!map.value) return
  animate({ zoom: map.value.getView().getZoom() - 1 })
}

const zoomTo = zoom => {
  animate({ zoom })
}

const panTo = center => {
  animate({ center })
}

// RED-236 — focus a coordinate when re-opening a transaction from the "Viimati
// vaadatud" panel. On a fresh page load the whole country is in view (zoom ~2)
// and the halo alone is easy to miss, so when the map is still at an overview
// zoom we pull in to a detail zoom (12 — the same single-point focus zoom used
// by addGeometryFeature / address search) and centre the marker. When the user
// is already zoomed in we only re-centre: re-zooming a comfortable view is
// jarring (the "jiggly" behaviour the panel must avoid).
const RECENTLY_VIEWED_OVERVIEW_MAX_ZOOM = 10
const RECENTLY_VIEWED_FOCUS_ZOOM = 12
const focusCoordinate = coord => {
  if (!map.value || !coord) {
    return
  }
  const currentZoom = map.value.getView().getZoom()
  if (currentZoom != null && currentZoom < RECENTLY_VIEWED_OVERVIEW_MAX_ZOOM) {
    animate({ center: coord, zoom: RECENTLY_VIEWED_FOCUS_ZOOM })
  } else {
    animate({ center: coord })
  }
}

// ── RED-236: viewed badge re-apply + open-marker halo ──────────────────────

// Re-stamp the `viewed` flag on the features whose membership actually
// changed since the last run (usually one) and force a re-render. Freshly
// loaded features are stamped in transformProperties, and rebuildFeatureIndex
// resets the diff baseline, so the two never drift. The old full
// getFeatures().forEach fired a change event per feature — 35k+ on a
// countrywide load — for every single transaction open.
const applyViewedState = () => {
  if (!props.recentlyViewedEnabled || !featureSource.value) {
    return
  }
  const ids = viewedIds.value
  let dirty = false
  const restamp = id => {
    const feature = featureIndex.get(String(id))
    if (feature && !feature.get('cluster')) {
      feature.set('viewed', ids.has(id) ? 1 : 0, true)
      dirty = true
    }
  }
  ids.forEach(id => {
    if (!lastAppliedViewedIds.has(id)) {
      restamp(id)
    }
  })
  lastAppliedViewedIds.forEach(id => {
    if (!ids.has(id)) {
      restamp(id)
    }
  })
  lastAppliedViewedIds = ids
  if (dirty) {
    featureSource.value.changed()
    featureLayer.value?.changed?.()
  }
}

// A single DOM overlay renders the strong, own-colour, pulsing halo behind the
// currently-open marker. A DOM overlay (not a WebGL/Canvas style) keeps it
// robust across render paths and easy to make prominent (Toomas RED-236: make
// the halo stronger). Never uses indigo — that hue is reserved for search.
const openHaloOverlay = shallowRef(null)
const openHaloGlow = shallowRef(null)

// Pixels to lift the halo from the marker's coordinate (the pin tip) up onto
// the marker head — see the anchor note where the overlay is created (a.1).
const HALO_HEAD_OFFSET_Y = -20

const ensureHaloOverlay = () => {
  if (openHaloOverlay.value || !map.value) {
    return
  }
  const root = document.createElement('div')
  root.style.pointerEvents = 'none'
  const glow = document.createElement('div')
  Object.assign(glow.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    width: '58px',
    height: '58px',
    transform: 'translate(-50%, -50%)',
    borderRadius: '50%',
    pointerEvents: 'none',
  })
  root.appendChild(glow)
  const overlay = new Overlay({
    element: root,
    positioning: 'center-center',
    // The marker icon is anchored at its bottom tip ([0.5, 1] on a 28×32
    // viewBox), so the transaction's coordinate sits at the pin tip — ~20px
    // below the visible marker head. Shift the halo up by that amount so it
    // sits centred on the head, not floating below it (RED-236 a.1 offset bug).
    offset: [0, HALO_HEAD_OFFSET_Y],
    stopEvent: false,
    insertFirst: true,
  })
  map.value.addOverlay(overlay)
  openHaloOverlay.value = overlay
  openHaloGlow.value = glow
  glow.animate(
    [
      { transform: 'translate(-50%,-50%) scale(0.9)', opacity: 0.95 },
      { transform: 'translate(-50%,-50%) scale(1.14)', opacity: 0.5 },
      { transform: 'translate(-50%,-50%) scale(0.9)', opacity: 0.95 },
    ],
    { duration: 2000, iterations: Infinity, easing: 'ease-in-out' },
  )
}

// Own-hue for the open marker — mirrors the marker colour vocabulary and
// iconVariant() priority (comparison > non-free > ad-linked > ad-not-found >
// default). Never indigo.
const openHaloColorFor = t => {
  if (!t) {
    return '#027BFA'
  }
  if (markedTransactions.value?.includes?.(t.id)) {
    return '#DB3848'
  }
  if (t.not_vabaturu_tehing) {
    return '#FF4081'
  }
  if (t.ad_link) {
    return '#2DA74E'
  }
  if (t.ad_link_not_found) {
    return '#FFC107'
  }
  return '#027BFA'
}

// Prefer the live feature's exact position; fall back to the coordinate stored
// with the recently-viewed entry (used when re-opening from the panel before
// the marker has (re)loaded into view).
const openHaloCoordFor = t => {
  const geom = featureIndex.get(String(t.id))?.getGeometry?.()
  if (geom) {
    return geom.getCoordinates()
  }
  const rec = recentlyViewedStore.items.find(i => i.id === t.id)
  return rec?.coord || null
}

// Record an opened transaction into the recently-viewed list. Lives here (not
// in the shared store action) so it is naturally gated to the standalone map
// and can read the marker's exact coordinate from the live feature.
const recordViewed = t => {
  if (!props.recentlyViewedEnabled || !t || t.id == null) {
    return
  }
  recentlyViewedStore.record({
    id: t.id,
    aadress: t.aadress,
    kpv: t.kpv,
    tehingusumma: t.tehingusumma,
    ad_link: t.ad_link,
    ad_link_not_found: t.ad_link_not_found,
    not_vabaturu_tehing: t.not_vabaturu_tehing,
    coord: openHaloCoordFor(t),
  })
}

const updateOpenHalo = t => {
  if (!props.recentlyViewedEnabled || !map.value) {
    return
  }
  if (!t || t.id == null) {
    openHaloOverlay.value?.setPosition(undefined)
    return
  }
  ensureHaloOverlay()
  const coord = openHaloCoordFor(t)
  if (!coord) {
    openHaloOverlay.value?.setPosition(undefined)
    return
  }
  const color = openHaloColorFor(t)
  if (openHaloGlow.value) {
    // ~20% stronger than the original (Toomas RED-236 "make the halo stronger"):
    // gradient alphas 73→8A / 30→3A and box-shadow alpha 59→6B, blur/spread
    // 16px/5px → 19px/6px. Same own-hue + pulse; never indigo (reserved for search).
    openHaloGlow.value.style.background = `radial-gradient(circle, transparent 30%, ${color}8A 44%, ${color}3A 60%, transparent 80%)`
    openHaloGlow.value.style.boxShadow = `0 0 19px 6px ${color}6B`
  }
  openHaloOverlay.value.setPosition(coord)
}

watch(viewedIds, () => applyViewedState())
watch(selectedTransaction, t => {
  // Record first (updates viewedIds → applyViewedState paints the badge), then
  // position the halo. Both are no-ops unless recentlyViewedEnabled.
  recordViewed(t)
  updateOpenHalo(t)
})

const handleMoveEnd = debounce(async () => {
  if (!map.value) return

  const view = map.value.getView()
  const center = view.getCenter()
  const zoom = view.getZoom()
  const extent = view.calculateExtent(map.value.getSize())

  // Update heatmap blur/radius based on new zoom level
  updateHeatmapParams(zoom)

  // No abort here: a pure pan must never kill a healthy in-flight load.
  // loadFeatures aborts the previous request itself when it issues a new one.

  emit('viewport-change', {
    center,
    zoom,
    extent,
  })
}, 200)

const selectedFeature = shallowRef(null)

const enableClickSelect = () => {
  map.value.on('click', handleClick)
  map.value.on('pointermove', handleHoverMove)
  map.value.on('movestart', clearHover)
}

const toggleMark = transaction => {
  // console.log('toggleMark', transaction)

  // console.log(featureSource.value)
  const feature = featureSource.value.getClosestFeatureToCoordinate(
    transaction.geometry.geometries[0].coordinates,
  )
  // console.log('feature', feature)
  // Use 'icon' property (transformed from _icon to avoid WebGL GLSL issues)
  const icon = feature.get('icon')

  if (icon === 'marked_for_comparison') {
    feature.set('icon', 'default')
  } else {
    feature.set('icon', 'marked_for_comparison')
  }
  // map.value.render()
}

const handleClick = async evt => {
  // Markers win over parcel polygons when both are under the cursor.
  // Parcels resolve geometrically — clicking anywhere inside the polygon works.
  const clickedMarker = findMarkerAtPixel(evt.pixel)
  const clickedParcel =
    !clickedMarker && getParcelsAtCoordinate(evt.coordinate)[0]

  const collectedIds = []
  let isCluster = false
  let clickedFeature = null

  if (clickedMarker || clickedParcel) {
    const topFeature = clickedMarker || clickedParcel
    clickedFeature = topFeature

    if (topFeature.get('cluster')) {
      isCluster = true
      const pointIds = topFeature.get('point_ids')
      if (pointIds && pointIds.length > 0) {
        collectedIds.push(...pointIds)
      }
    } else {
      // A parcel polygon resolves to ALL its owning transactions — a shared
      // plot (apartment building) behaves like a cluster click
      const parcelOwners = topFeature.get('transaction_ids')
      if (parcelOwners?.length) {
        collectedIds.push(...parcelOwners)
      } else {
        const featureId = topFeature.get('id')
        if (featureId) {
          collectedIds.push(featureId)
        }
      }
    }
  }

  if (collectedIds.length > 0) {
    emit('features-selected', {
      ids: collectedIds,
      pixel: evt.pixel,
      isCluster: isCluster || collectedIds.length > 1,
      feature: clickedFeature,
    })
  } else {
    emit('features-selected', {
      ids: [],
      pixel: evt.pixel,
      isCluster: false,
      feature: null,
    })
  }
}

onBeforeUnmount(() => {
  // Cancel any pending API requests
  transactionStore.abortAndResetTransactionsAbortController()

  // Cancel debounced functions to prevent stale callbacks after unmount
  debouncedRefresh.cancel?.()
  handleMoveEnd.cancel?.()

  // Clean up feature source and layer
  featureIndex.clear()
  clusterByMemberId.clear()
  disposeParcels()
  if (hoverHighlightSource.value) {
    hoverHighlightSource.value.clear()
    hoverHighlightSource.value.dispose?.()
    hoverHighlightSource.value = null
  }
  if (hoverHighlightLayer.value) {
    hoverHighlightLayer.value.dispose?.()
    hoverHighlightLayer.value = null
  }
  if (featureSource.value) {
    featureSource.value.clear()
    featureSource.value.dispose?.()
    featureSource.value = null
  }
  if (featureLayer.value) {
    featureLayer.value.dispose?.()
    featureLayer.value = null
  }

  // Clean up label layer
  if (labelSource.value) {
    labelSource.value.clear()
    labelSource.value.dispose?.()
    labelSource.value = null
  }
  if (labelLayer.value) {
    labelLayer.value.dispose?.()
    labelLayer.value = null
  }

  // Clean up heatmap layer
  if (heatmapSource.value) {
    heatmapSource.value.clear()
    heatmapSource.value.dispose?.()
    heatmapSource.value = null
  }
  if (heatmapLayer.value) {
    heatmapLayer.value.dispose?.()
    heatmapLayer.value = null
  }

  // RED-236 — drop the open-marker halo overlay
  if (openHaloOverlay.value && map.value) {
    map.value.removeOverlay(openHaloOverlay.value)
  }
  openHaloOverlay.value = null
  openHaloGlow.value = null

  if (map.value) {
    map.value.un('moveend', handleMoveEnd)
    map.value.un('click', handleClick)
    map.value.un('pointermove', handleHoverMove)
    map.value.un('movestart', clearHover)
    map.value.dispose()
    map.value = null
  }
})

defineExpose({
  addFeaturesToMap,
  addGeometryFeature,
  addSearchHighlight,
  clearSearchHighlight,
  toggleMark,
  panTo,
  zoomTo,
  focusCoordinate,
})
</script>

<style>
  @reference '../../../css/tailwind.css';

  .map {
    width: 100%;
    height: 100%;
    position: relative;
    @apply dark:brightness-75 dark:saturate-50;
  }

  .map .zoom {
    position: absolute;
    top: 15px;
    left: 15px;
    z-index: 1;
  }

  .map .popup-wrapper,
  .map .popup-target {
    width: 1px;
    height: 1px;
  }

  .map .popup-wrapper .popup {
    width: 380px;
  }

  .map .popup-wrapper .popup .popover-body {
    padding: 0;
    max-height: 280px;
    overflow-y: auto;

    .list-group {
      border-radius: 0;
    }
  }
</style>
